import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Chip, Collapse, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, IconButton, List, ListItemButton,
  ListItemText, Paper, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import FolderIcon from '@mui/icons-material/Folder';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LanOutlinedIcon from '@mui/icons-material/LanOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';

import api from '../../../../services/api';
import EmpresaSelect from '../../../../components/EmpresaSelect';
import { useNotify } from '../../../../components/Datatable/NotifyProvider';
import GrupoDialog, { RemoverGrupoDialog } from './GrupoDialog';
import SeletorDeHosts from './SeletorDeHosts';
import { SUPERFICIE, getItem } from './estilo';

const ROTA = '/admin/snapshot';



/** Nó da árvore. Não há componente de árvore no projeto, então é Collapse
 *  aninhado com indentação — mesma abordagem do ItemMenu da sidebar. */
function No({ no, nivel, selecionado, onSelecionar }) {
  const [aberto, setAberto] = useState(nivel < 2);
  const temFilhos = no.children?.length > 0;

  return (
    <>
      <ListItemButton
        selected={selecionado?.id === no.id}
        onClick={() => onSelecionar(no)}
        sx={{ pl: 1 + nivel * 2.5, borderRadius: 1 }}
      >
        {temFilhos ? (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setAberto((v) => !v);
            }}
            sx={{ mr: 0.5 }}
          >
            {aberto ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
          </IconButton>
        ) : (
          <Box sx={{ width: 34 }} />
        )}
        <FolderIcon fontSize="small" sx={{ mr: 1, color: '#94a3b8' }} />
        <ListItemText
          primary={no.name}
          secondary={no.description || null}
          primaryTypographyProps={{ fontSize: 14 }}
          secondaryTypographyProps={{ fontSize: 12 }}
        />
        <Tooltip title="Hosts diretamente neste grupo">
          <Chip size="small" label={no.host_count} sx={{ height: 20, ml: 1 }} />
        </Tooltip>
      </ListItemButton>

      {temFilhos && (
        <Collapse in={aberto} timeout="auto" unmountOnExit>
          {no.children.map((f) => (
            <No
              key={f.id}
              no={f}
              nivel={nivel + 1}
              selecionado={selecionado}
              onSelecionar={onSelecionar}
            />
          ))}
        </Collapse>
      )}
    </>
  );
}

export default function GruposHosts() {
  const notify = useNotify();

  const [empresa, setEmpresa] = useState(getItem(localStorage, 'hermes')?.empresa || null);
  const [arvore, setArvore] = useState([]);
  const [sel, setSel] = useState(null);
  const [previa, setPrevia] = useState(null);
  const [carregando, setCarregando] = useState(false);

  const [dlgNovo, setDlgNovo] = useState(false);
  const [nome, setNome] = useState('');
  const [descricao, setDescricao] = useState('');
  const [paiId, setPaiId] = useState(null);

  const [dlgMover, setDlgMover] = useState(false);
  const [aRemover, setARemover] = useState(null);

  // Arvore achatada para os seletores de "pai". A indentacao preserva a
  // hierarquia — nao ha componente de arvore em campo de selecao no projeto.
  const gruposFlat = useMemo(() => {
    const out = [];
    const walk = (nos, nivel) => {
      nos.forEach((n) => {
        out.push({ ...n, label: `${' '.repeat(nivel * 4)}${n.name}`, nivel });
        if (n.children?.length) walk(n.children, nivel + 1);
      });
    };
    walk(arvore, 0);
    return out;
  }, [arvore]);
  const [novoPai, setNovoPai] = useState(null);

  const [diretos, setDiretos] = useState([]);
  const [diretosOriginais, setDiretosOriginais] = useState([]);

  // Só habilita o salvar quando algo mudou de verdade — botão sempre ativo
  // convida a gravar sem querer numa tela que pode reorganizar o parque.
  const alterouDiretos = useMemo(() => {
    if (diretos.length !== diretosOriginais.length) return true;
    const orig = new Set(diretosOriginais);
    return diretos.some((id) => !orig.has(id));
  }, [diretos, diretosOriginais]);

  useEffect(() => {
    document.title = 'IPV7 Hermes - Grupos de Hosts';
  }, []);

  const carregar = useCallback(async () => {
    if (!empresa) return;
    setCarregando(true);
    try {
      // Só a árvore. Os equipamentos são buscados sob demanda, paginados —
      // carregar os 2 mil de uma vez era o que quebrava em parque grande.
      const t = await api.get(`${ROTA}/group/tree`, { params: { empresa } });
      setArvore(t.data?.data || []);
    } catch {
      notify.setNotify({ open: true, message: 'Erro ao carregar grupos', severity: 'error' });
    } finally {
      setCarregando(false);
    }
    // notify vem de um Context estável; incluí-lo causaria recarga extra.
  }, [empresa]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  // Achata a árvore para os selects de "grupo pai".
  const plano = useMemo(() => {
    const out = [];
    const walk = (nos, nivel) => {
      nos.forEach((n) => {
        out.push({ ...n, label: `${' '.repeat(nivel * 4)}${n.name}`, nivel });
        if (n.children?.length) walk(n.children, nivel + 1);
      });
    };
    walk(arvore, 0);
    return out;
  }, [arvore]);

  const selecionar = async (no) => {
    setSel(no);
    setPrevia(null);
    try {
      const res = await api.get(`${ROTA}/group/${no.id}/hosts/preview`, { params: { empresa } });
      if (res.data?.data) {
        setPrevia(res.data.data);
        // Só os DIRETOS entram no editor — os expandidos são informativos.
        const d = res.data.data.direct_host_ids || [];
        setDiretos(d);
        setDiretosOriginais(d);
      }
    } catch {
      notify.setNotify({ open: true, message: 'Erro ao carregar hosts do grupo', severity: 'error' });
    }
  };

  const criar = async () => {
    try {
      const res = await api.post(`${ROTA}/group/create`, {
        empresa,
        name: nome,
        description: descricao || null,
        parent_id: paiId || null,
      });
      if (res.data?.errors) {
        notify.setNotify({ open: true, message: res.data.errors[0].msg, severity: 'error' });
        return;
      }
      setDlgNovo(false);
      setNome('');
      setDescricao('');
      setPaiId(null);
      carregar();
      notify.setNotify({ open: true, message: 'Grupo criado', severity: 'success' });
    } catch {
      notify.setNotify({ open: true, message: 'Erro ao criar grupo', severity: 'error' });
    }
  };

  const mover = async () => {
    try {
      const res = await api.put(`${ROTA}/group/${sel.id}/move`, {
        empresa,
        parent_id: novoPai?.id || null,
      });
      if (res.data?.errors) {
        // Ciclo ou profundidade: erro de negócio, com mensagem específica.
        notify.setNotify({ open: true, message: res.data.errors[0].msg, severity: 'error' });
        return;
      }
      if (res.data?.error) {
        notify.setNotify({ open: true, message: res.data.error, severity: 'error' });
        return;
      }
      setDlgMover(false);
      setNovoPai(null);
      setSel(null);
      carregar();
      notify.setNotify({ open: true, message: 'Grupo movido', severity: 'success' });
    } catch {
      notify.setNotify({ open: true, message: 'Erro ao mover grupo', severity: 'error' });
    }
  };

  const salvarHosts = async () => {
    try {
      const res = await api.put(`${ROTA}/group/${sel.id}/hosts`, {
        empresa,
        host_ids: diretos,
      });
      if (res.data?.errors) {
        notify.setNotify({ open: true, message: res.data.errors[0].msg, severity: 'error' });
        return;
      }
      carregar();
      selecionar(sel);
      notify.setNotify({ open: true, message: 'Hosts atualizados', severity: 'success' });
    } catch {
      notify.setNotify({ open: true, message: 'Erro ao salvar hosts', severity: 'error' });
    }
  };

  return (
    <Box sx={{ p: 2, minHeight: '100%', bgcolor: '#f1f5f9' }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ color: '#0f172a' }}>
          Grupos de Hosts
        </Typography>
        <Box sx={{ flex: 1 }} />
        <EmpresaSelect
          sx={{ width: 380, ...SUPERFICIE, borderRadius: 1 }}
          value={empresa}
          handler={(e) => {
            localStorage.setItem('hermes', JSON.stringify({ empresa: e }));
            setEmpresa(e);
            setSel(null);
            setPrevia(null);
          }}
        />
        <Button onClick={carregar} disabled={carregando} startIcon={<RefreshIcon />}>
          Atualizar
        </Button>
      </Stack>

      {!empresa && <Alert severity="info">Selecione uma empresa para começar.</Alert>}

      {empresa && (
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
          {/* Árvore */}
          <Paper variant="outlined" sx={{ p: 1.5, flex: 1, minWidth: 320, ...SUPERFICIE }}>
            <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle2">Hierarquia</Typography>
              <Box sx={{ flex: 1 }} />
              <Button size="small" startIcon={<AddIcon />} onClick={() => setDlgNovo(true)}>
                Novo grupo
              </Button>
            </Stack>
            <Divider sx={{ mb: 1 }} />

            {arvore.length === 0 ? (
              <Alert severity="info" sx={{ mt: 1 }}>
                Nenhum grupo ainda. Crie o primeiro para começar a organizar os hosts.
              </Alert>
            ) : (
              <List dense disablePadding>
                {arvore.map((n) => (
                  <No key={n.id} no={n} nivel={0} selecionado={sel} onSelecionar={selecionar} />
                ))}
              </List>
            )}
          </Paper>

          {/* Detalhe do grupo selecionado */}
          <Paper variant="outlined" sx={{ p: 2, flex: 1.4, minWidth: 380, ...SUPERFICIE }}>
            {!sel ? (
              <Typography variant="body2" sx={{ color: '#64748b' }}>
                Selecione um grupo à esquerda.
              </Typography>
            ) : (
              <>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {sel.name}
                  </Typography>
                  <Box sx={{ flex: 1 }} />
                  <Button
                    size="small"
                    startIcon={<DriveFileMoveIcon />}
                    onClick={() => setDlgMover(true)}
                  >
                    Mover
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />}
                    onClick={() => setARemover(sel)}
                  >
                    Remover
                  </Button>
                </Stack>

                <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                  {sel.path} · nível {sel.depth}
                </Typography>

                {/* A prévia é o que um run vai CONGELAR: inclui os hosts da
                    subárvore, não só os diretos. Vale conferir antes de coletar. */}
                {previa && (
                  <Alert severity="info" icon={<LanOutlinedIcon />} sx={{ mt: 1.5, mb: 1.5 }}>
                    Este grupo expande para <strong>{previa.total} host(s)</strong>, incluindo os
                    das subárvores. É essa lista que uma coleta vai congelar.
                  </Alert>
                )}

                <Divider sx={{ my: 1.5 }} />

                {/* DIRETOS, não expandidos.
                    Este campo carregava a lista EXPANDIDA — que inclui a
                    subárvore — sob o rótulo "diretamente neste grupo". Salvar
                    dali gravava como membro direto tudo que pertencia aos
                    filhos: um clique no grupo raiz achatava a hierarquia
                    inteira, em silêncio. */}
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="subtitle2">Equipamentos deste grupo</Typography>
                  <Chip size="small" label={`${diretos.length} direto(s)`}
                    sx={{ height: 19, bgcolor: '#e2e8f0', color: '#475569' }} />
                  {previa && previa.total > diretos.length && (
                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                      + {previa.total - diretos.length} vindo(s) das subárvores
                    </Typography>
                  )}
                </Stack>

                <SeletorDeHosts
                  empresa={empresa}
                  selecionados={diretos}
                  aoMudar={setDiretos}
                  altura="30vh"
                />

                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }}>
                  <Button
                    variant="contained" size="small"
                    onClick={salvarHosts} disabled={!alterouDiretos}
                  >
                    Salvar
                  </Button>
                  {alterouDiretos && (
                    <Button size="small" onClick={() => setDiretos(diretosOriginais)}>
                      desfazer
                    </Button>
                  )}
                  {alterouDiretos && (
                    <Typography variant="caption" sx={{ color: '#d97706' }}>
                      alterações não salvas
                    </Typography>
                  )}
                </Stack>
              </>
            )}
          </Paper>
        </Stack>
      )}

      <GrupoDialog
        aberto={dlgNovo}
        empresa={empresa}
        grupos={gruposFlat}
        paiSugerido={sel}
        onFechar={() => setDlgNovo(false)}
        onCriado={carregar}
      />

      <RemoverGrupoDialog
        grupo={aRemover}
        empresa={empresa}
        onFechar={() => setARemover(null)}
        onRemovido={() => { setSel(null); carregar(); }}
      />


      {/* Mover grupo */}
      <Dialog open={dlgMover} onClose={() => setDlgMover(false)} fullWidth maxWidth="sm">
        <DialogTitle>Mover “{sel?.name}”</DialogTitle>
        <DialogContent>
          <Autocomplete
            sx={{ mt: 1 }}
            options={plano.filter((p) => p.id !== sel?.id)}
            value={novoPai}
            onChange={(_, v) => setNovoPai(v)}
            getOptionLabel={(o) => o.label || o.name}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            renderInput={(p) => (
              <TextField
                {...p}
                size="small"
                label="Novo grupo pai (vazio = raiz)"
                helperText="Mover para dentro da própria subárvore é bloqueado pelo servidor."
              />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlgMover(false)}>Cancelar</Button>
          <Button variant="contained" onClick={mover}>
            Mover
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
